import { redirect } from "@tanstack/react-router";
import {
	type EntityTypeFormMode,
	useEntityTypeFormModeStore,
} from "@/features/form-editor/state/entity-type-form-mode-store";

export function drawerFormSearch(
	search: unknown,
	fallbackPresentation: "drawer" | "full",
): { mode: "view" | "edit"; presentation: "drawer" | "modal" | "full" } {
	const raw =
		search && typeof search === "object"
			? (search as Record<string, unknown>)
			: {};
	const presentation =
		raw.presentation === "drawer" ||
		raw.presentation === "modal" ||
		raw.presentation === "full"
			? raw.presentation
			: fallbackPresentation;
	const mode = raw.mode === "edit" ? "edit" : "view";
	return { mode, presentation };
}

export function legacyFormRouteMode(pathname: string): EntityTypeFormMode {
	return pathname.replace(/\/$/, "").endsWith("/rules") ? "rules" : "build";
}

/**
 * Search for `/full?tab=form` after an old `/full/form` URL.
 * Keeps rule-modal fields the form rules panel still reads.
 */
export function formTabSearch(search: unknown): Record<string, unknown> {
	const raw =
		search && typeof search === "object"
			? (search as Record<string, unknown>)
			: {};
	const presentation =
		raw.presentation === "drawer" ||
		raw.presentation === "modal" ||
		raw.presentation === "full"
			? raw.presentation
			: "full";
	const mode = raw.mode === "edit" ? "edit" : "view";
	const next: Record<string, unknown> = {
		tab: "form",
		mode,
		presentation,
	};
	if (typeof raw.ruleId === "string" && raw.ruleId.length > 0) {
		next.ruleId = raw.ruleId;
	}
	if (
		raw.ruleMode === "create" ||
		raw.ruleMode === "edit" ||
		raw.ruleMode === "delete"
	) {
		next.ruleMode = raw.ruleMode;
	}
	if (raw.ruleTab === "details" || raw.ruleTab === "activity") {
		next.ruleTab = raw.ruleTab;
	}
	if (
		typeof raw.subOrganizationId === "string" &&
		raw.subOrganizationId.length > 0
	) {
		next.subOrganizationId = raw.subOrganizationId;
	}
	return next;
}

/**
 * Old builder URLs land on the Form tab and select Build or Rules.
 */
export function redirectLegacyEntityTypeForm(input: {
	pathname: string;
	typeId: string;
	to: string;
	params: Record<string, string>;
	search: Record<string, unknown>;
}): never {
	useEntityTypeFormModeStore
		.getState()
		.setMode(input.typeId, legacyFormRouteMode(input.pathname));
	throw redirect({
		to: input.to as never,
		params: input.params as never,
		search: input.search as never,
		replace: true,
	});
}
